/**
 * Subscription Product Page JavaScript
 *
 * Handles:
 * - Operator-targeted upsell visibility based on user meta (logged in) or localStorage (guest)
 * - Plan details accordion toggle
 * - Grouped membership cards visibility filtering
 *
 * @package bricks-arival
 */

(function() {
	'use strict';

	/**
	 * Subscription Product Handler
	 */
	var SubscriptionProduct = {

		/**
		 * Configuration
		 */
		config: {
			storageKey: 'arival_membership_filter_selection',
			upsellSelector: '.subscription-upsell-section[data-operator-type]',
			containerSelector: '.subscription-upsells-container',
			accordionToggleSelector: '.plan-details-toggle',
			accordionContentSelector: '.plan-details-content',
			membershipCardsContainer: '.membership-cards-container',
			membershipCardSelector: '.membership-card[data-visibility]'
		},

		/**
		 * Track initialization state
		 */
		initialized: false,

		/**
		 * Initialize
		 */
		init: function() {
			// Prevent double initialization
			if (this.initialized) {
				return;
			}
			this.initialized = true;

			// Initialize for subscription product pages
			if (document.querySelector('.subscription-membership-template')) {
				this.initUpsellVisibility();
				this.initPlanDetailsAccordion();
			}

			// Initialize for grouped membership pages
			if (document.querySelector('.grouped-membership-template')) {
				this.initMembershipCardsVisibility();
				this.initPlanDetailsAccordion();
			}
		},

		/**
		 * Get operator type - checks server-provided user meta first (for logged-in users),
		 * then falls back to localStorage (for guests or if no user meta set)
		 *
		 * @return {string|null} 'operator', 'non-operator', or null
		 */
		getOperatorType: function() {
			// First check server-provided data for logged-in users
			if (typeof arivalSubscriptionData !== 'undefined' && arivalSubscriptionData.userOperatorType) {
				return arivalSubscriptionData.userOperatorType;
			}

			// Fall back to localStorage for guests or if no server value
			try {
				return localStorage.getItem(this.config.storageKey);
			} catch (e) {
				return null;
			}
		},

		/**
		 * Initialize upsell visibility based on operator type
		 */
		initUpsellVisibility: function() {
			var container = document.querySelector(this.config.containerSelector);
			if (!container) {
				return;
			}

			var upsells = container.querySelectorAll(this.config.upsellSelector);
			if (upsells.length === 0) {
				return;
			}

			var operatorType = this.getOperatorType();
			var hasVisibleUpsell = false;

			upsells.forEach(function(upsell) {
				var targetType = upsell.getAttribute('data-operator-type');
				var shouldShow = false;

				if (operatorType) {
					// User has an operator type set - show matching upsells or 'all' type
					shouldShow = (targetType === operatorType) || (targetType === 'all');
				} else {
					// No operator type set - only show 'all' type (fallback)
					shouldShow = (targetType === 'all');
				}

				if (shouldShow) {
					upsell.style.display = '';
					upsell.classList.add('upsell-visible');
					hasVisibleUpsell = true;
				} else {
					upsell.style.display = 'none';
					upsell.classList.remove('upsell-visible');
				}
			});

			// Mark container as initialized
			container.setAttribute('data-upsells-initialized', 'true');

			// Hide container if no visible upsells
			if (!hasVisibleUpsell) {
				container.style.display = 'none';
			}
		},

		/**
		 * Initialize membership cards visibility for grouped products
		 * Filters cards based on operator type (same logic as upsells)
		 */
		initMembershipCardsVisibility: function() {
			var container = document.querySelector(this.config.membershipCardsContainer);
			if (!container) {
				return;
			}

			var cards = container.querySelectorAll(this.config.membershipCardSelector);
			if (cards.length === 0) {
				return;
			}

			var operatorType = this.getOperatorType();

			cards.forEach(function(card) {
				var cardVisibility = card.getAttribute('data-visibility');
				var shouldShow = false;

				if (operatorType) {
					// User has an operator type set - show matching cards or 'all' type
					shouldShow = (cardVisibility === operatorType) || (cardVisibility === 'all');
				} else {
					// No operator type set - only show 'all' type (fallback)
					shouldShow = (cardVisibility === 'all');
				}

				if (shouldShow) {
					card.setAttribute('data-hidden', 'false');
				} else {
					card.setAttribute('data-hidden', 'true');
				}
			});

			// Mark container as initialized
			container.setAttribute('data-initialized', 'true');
		},

		/**
		 * Initialize plan details accordion
		 */
		initPlanDetailsAccordion: function() {
			var toggleButtons = document.querySelectorAll(this.config.accordionToggleSelector);

			toggleButtons.forEach(function(toggle) {
				// Skip if already initialized
				if (toggle.hasAttribute('data-accordion-initialized')) {
					return;
				}

				var content = toggle.nextElementSibling;

				if (!content || !content.classList.contains('plan-details-content')) {
					return;
				}

				// Mark as initialized to prevent double binding
				toggle.setAttribute('data-accordion-initialized', 'true');

				// Ensure accordion starts closed
				toggle.setAttribute('aria-expanded', 'false');
				content.setAttribute('aria-hidden', 'true');
				content.style.maxHeight = '0';

				// Add click handler
				toggle.addEventListener('click', function(e) {
					var isExpanded = toggle.getAttribute('aria-expanded') === 'true';

					if (isExpanded) {
						// Close accordion
						toggle.setAttribute('aria-expanded', 'false');
						content.setAttribute('aria-hidden', 'true');
						content.style.maxHeight = '0';
					} else {
						// Open accordion
						toggle.setAttribute('aria-expanded', 'true');
						content.setAttribute('aria-hidden', 'false');
						content.style.maxHeight = content.scrollHeight + 'px';
					}
				});

				// Handle dynamic content height changes
				var resizeObserver = new ResizeObserver(function(entries) {
					for (var i = 0; i < entries.length; i++) {
						if (toggle.getAttribute('aria-expanded') === 'true') {
							content.style.maxHeight = content.scrollHeight + 'px';
						}
					}
				});

				resizeObserver.observe(content);
			});
		}
	};

	// Initialize on DOM ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function() {
			SubscriptionProduct.init();
		});
	} else {
		SubscriptionProduct.init();
	}

	// Expose for external access if needed
	window.ArivalSubscriptionProduct = SubscriptionProduct;

})();
